import { useParams, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { StyledListingDetailPage } from './style';
import { StatusTag } from '../../components/ListingTags/style';
import { ListingPublicationPeriod } from '../../components/ListingPublicationPeriod';
import { Button, Icon, Tabs, Divider, Spinner } from '../../../ui';
import PublicRoute from 'enums/publicRoutes';
import { adminPath } from 'enums/adminRoutes';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useState, useEffect } from 'react';
import { ContractDetailsTab } from './tabs/ContractDetailsTab';
import { ProductsTab } from './tabs/ProductsTab';
import { DeliveryConditionsTab } from './tabs/DeliveryConditionsTab';
import { permissions } from '../../../config/config';
import { Listing } from './types';
import { DefaultThirdHeader } from '../../components/DefaultLayout/DefaultThirdHeader';
import FarmRatings from '../FarmPage/components/Ratings';
import { FarmRatingsProps } from '../FarmPage/components/Ratings/types';
import { Rate, message } from 'antd';
import { TooltipWithIcon } from 'ui';
import dayjs from 'dayjs';
import { useAuth } from 'hooks/useAuth';
import { GROUPED_QUALITY_MARKS_URL } from 'utils/qualityMarkHelpers';

export const ListingDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const intl = useIntl();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [listing, setListing] = useState<Listing | null>(null);
  const [, setDataLoaded] = useState(false);
  const [farmRatingsData, setFarmRatingsData] = useState<
    FarmRatingsProps['farmRatingsData']
  >({
    total_ratings: 0,
    overall_average: 0,
    product_quality_average: 0,
    delivery_average: 0,
    communication_average: 0,
    payments_average: 0,
    ratings: [],
  });

  const { isLoading, appendData } = useQueryApiClient({
    request: {
      url: 'api/v2/wholesale/listings/:id',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setListing(response);
      setDataLoaded(true);
    },
  });

  const { data: allQualityMarks } = useQueryApiClient({
    request: {
      url: GROUPED_QUALITY_MARKS_URL,
    },
    enabled: !!listing,
  });

  const { appendData: fetchFarmRatings } = useQueryApiClient({
    request: {
      url: 'api/v2/farms/:farmId/ratings',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setFarmRatingsData(response);
    },
  });

  useEffect(() => {
    if (listing?.farm_id) {
      fetchFarmRatings({}, { farmId: listing.farm_id });
    }
  }, [listing?.farm_id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (id) {
      appendData({}, { id });
    }
  }, [id]);

  if (!listing && isLoading) {
    return (
      <StyledListingDetailPage>
        <Spinner spinning={true}>
          <div />
        </Spinner>
      </StyledListingDetailPage>
    );
  }

  if (!listing) {
    return (
      <StyledListingDetailPage>
        <div>{intl.formatMessage({ id: 'listing.not_found' })}</div>
      </StyledListingDetailPage>
    );
  }

  const getDeliveryPlace = () => {
    return listing.address || null;
  };

  const getEditedText = () => {
    if (!listing.updated_by_user_at) {
      return null;
    }
    const transactionTypeText = intl.formatMessage({
      id: `listing.edited.${listing.transaction_type}`,
    });
    const editedText = intl.formatMessage({ id: 'listing.edited' });
    return `${transactionTypeText} ${editedText} ${dayjs(listing.updated_by_user_at).format('DD.MM.YYYY')}`;
  };

  const getButtonLabel = () => {
    if (listing.farm_application_time_at || listing.is_pending_submitting) {
      return `${intl.formatMessage({ id: 'listing.application.created' })}: ${dayjs(listing.farm_application_time_at).format('DD.MM.YYYY')}`;
    }

    return intl.formatMessage({ id: 'listing.apply' });
  };

  const handleOpenListing = () => {
    const hasWholesaleContract = user?.farm?.has_wholesale_contract ?? false;
    const isApplying =
      !listing.farm_application_id && !listing.is_pending_submitting;

    if (isApplying && !user?.isAdmin && !hasWholesaleContract) {
      message.warning(
        intl.formatMessage({
          id: 'message.wholesale_contract_required_for_listings',
        })
      );
      navigate(adminPath.farmContracts());
      return;
    }

    const hasDynamicContract = user?.farm?.has_dynamic_contract ?? false;

    if (
      isApplying &&
      !user?.isAdmin &&
      listing.transaction_type === 'dynamic_purchase' &&
      !hasDynamicContract
    ) {
      message.warning(
        intl.formatMessage({
          id: 'message.dynamic_contract_required_for_listings',
        })
      );
      return;
    }

    if (listing.farm_application_id) {
      navigate(
        `${adminPath.listings()}?applicationId=${listing.farm_application_id}`
      );
    } else {
      navigate(`${adminPath.listings()}?listingId=${listing.id}`);
    }
  };

  const breadcrumbItems = [
    {
      name: intl.formatMessage({ id: 'general.start' }),
      route: PublicRoute.HOME,
    },
    {
      name: intl.formatMessage({ id: 'listing.breadcrumb.listings' }),
      route: PublicRoute.WHOLESALE_LISTINGS,
    },
    {
      name: listing.title,
      route: '',
    },
  ];

  const tabItems = [
    {
      key: 'contract-terms',
      label: intl.formatMessage({ id: 'listing.tabs.contract_terms' }),
      children: (
        <ContractDetailsTab
          listingData={{
            id: listing.id,
            contract_terms: listing.contract_terms,
          }}
        />
      ),
    },
    {
      key: 'products',
      label: intl.formatMessage({ id: 'listing.tabs.products' }),
      children: (
        <ProductsTab
          products={listing.products}
          allQualityMarks={allQualityMarks}
        />
      ),
    },
    {
      key: 'delivery',
      label: intl.formatMessage({ id: 'listing.tabs.delivery_conditions' }),
      children: (
        <DeliveryConditionsTab
          deliveryConditions={listing.delivery_conditions}
        />
      ),
    },
    ...(farmRatingsData.total_ratings > 0
      ? [
          {
            key: 'ratings',
            label: (
              <span className="ratings-tab-label">
                {intl.formatMessage({ id: 'listing.tabs.ratings' })}
                {farmRatingsData.overall_average > 0 && (
                  <Rate
                    disabled
                    allowHalf
                    value={farmRatingsData.overall_average}
                  />
                )}
              </span>
            ),
            children: <FarmRatings farmRatingsData={farmRatingsData} />,
          },
        ]
      : []),
  ];

  return (
    <StyledListingDetailPage>
      <DefaultThirdHeader
        label={listing.title}
        breadcrumb={breadcrumbItems}
        customClassName="listing-detail-header"
        rightContent={
          <>
            <span className="identifier">{listing.identifier}</span>
            <div>
              <StatusTag $status={listing.status}>
                {intl.formatMessage({
                  id: `listing.status.public.${listing.status}`,
                })}
              </StatusTag>
              {listing.status === 'cancelled' && (
                <TooltipWithIcon
                  title={
                    listing.cancel_reason ? (
                      <>
                        {intl.formatMessage({
                          id:
                            'enum.' +
                            listing.cancel_reason
                              .toUpperCase()
                              .replace(/-/g, '_'),
                          defaultMessage: listing.cancel_reason,
                        })}
                        {listing.cancel_reason === 'cancelled_other' &&
                          listing.cancel_reason_description && (
                            <>
                              <br />
                              {listing.cancel_reason_description}
                            </>
                          )}
                      </>
                    ) : (
                      intl.formatMessage({ id: 'listings.cancel_reason' })
                    )
                  }
                  placement="top"
                />
              )}
            </div>
          </>
        }
      />
      <div className="mobile-detail-header">
        <div
          className="mobile-back"
          onClick={() => navigate(PublicRoute.WHOLESALE_LISTINGS)}
        >
          <Icon name="arrow-left" className="back-icon" />
          <span className="back-text">
            {intl.formatMessage({ id: 'general.back_to_catalog' })}
          </span>
        </div>
        <h2 className="mobile-title">{listing.title}</h2>
        <div className="identifier-row">
          <span className="identifier">{listing.identifier}</span>
          <StatusTag $status={listing.status}>
            {intl.formatMessage({
              id: `listing.status.public.${listing.status}`,
            })}
          </StatusTag>
        </div>
        <div className="actions-row">
          <Button
            type="default"
            className="send-message-btn"
            label={intl.formatMessage({ id: 'listing.send_message' })}
            ability={permissions.edit_farm_listing}
            onClick={() =>
              navigate(
                `${adminPath.chat()}?listing_id=${listing.id}&recipient_farm_id=${listing.farm_id}`
              )
            }
          />
          <Button
            className="apply-btn"
            label={getButtonLabel()}
            onClick={handleOpenListing}
            ability={permissions.edit_farm_listing}
            disabled={
              listing.status !== 'published' && listing.status !== 'has_offers'
            }
          />
        </div>
      </div>
      <div className="container-content">
        {listing.description && (
          <>
            <div className="listing-description-row">
              <span className="description-label">
                {intl.formatMessage({ id: 'listing.description' })}:
              </span>
              <span className="description-text">{listing.description}</span>
            </div>
          </>
        )}

        <div className="description-divider">
          <Divider />
        </div>

        <div className="listing-content">
          <ListingPublicationPeriod
            publicationStartDate={listing.publication_start_date}
            publicationEndDate={listing.publication_end_date}
          />
          {getEditedText() && (
            <div className="edited-row">
              <span className="edited-text">{getEditedText()}</span>
            </div>
          )}

          <div className="info-and-actions">
            <div className="info-rows">
              <div className="transaction-type-row">
                <span className="type-label">
                  {intl.formatMessage({ id: 'listing.transaction_type_label' })}
                  :
                </span>
                <div className="type-content">
                  {intl.formatMessage({
                    id: `listing.transaction_type.public.${listing.transaction_type}`,
                  })}
                </div>
              </div>

              {getDeliveryPlace() && (
                <div className="delivery-row">
                  <span className="delivery-label">
                    {intl.formatMessage({ id: 'listing.delivery_place' })}:
                  </span>
                  <span className="delivery-text">{getDeliveryPlace()}</span>
                </div>
              )}
              {listing.farm_name && (
                <div className="farm-row">
                  <span className="farm-label">
                    {intl.formatMessage({ id: 'listing.farm_name' })}:
                  </span>
                  <span className="farm-text">{listing.farm_name}</span>
                </div>
              )}
            </div>

            <div className="actions-column">
              <Button
                label={getButtonLabel()}
                onClick={handleOpenListing}
                className="open-listing-button"
                ability={permissions.edit_farm_listing}
                disabled={
                  listing.status !== 'published' &&
                  listing.status !== 'has_offers'
                }
              />
              <Button
                type="link"
                className="send-message-link"
                label={intl.formatMessage({ id: 'listing.send_message' })}
                ability={permissions.edit_farm_listing}
                onClick={() => {
                  const params = new URLSearchParams({
                    listing_id: String(listing.id),
                    recipient_farm_id: String(listing.farm_id),
                    listing_title: listing.title,
                    listing_identifier: listing.identifier,
                  });
                  navigate(`${adminPath.chat()}?${params.toString()}`);
                }}
              />
            </div>
          </div>
        </div>

        <div className="listing-tabs">
          <Tabs items={tabItems} />
        </div>
      </div>
    </StyledListingDetailPage>
  );
};
